import { useState, useCallback, useEffect } from "react"
import type { Puzzle, User } from "@shared/types.ts"
import type { PuzzleType, GridStyle } from "@shared/constants.ts"
import { generatePuzzle, getCurrentUser, logout as apiLogout, getPuzzleBySlug, deletePuzzle, AuthError } from "./lib/api-client.ts"
import { loadLocal } from "./lib/puzzle-storage.ts"
import { usePuzzleState } from "./hooks/usePuzzleState.ts"
import { useKeyboardNav } from "./hooks/useKeyboardNav.ts"
import { useRouter } from "./hooks/useRouter.ts"
import { useProgressSync } from "./hooks/useProgressSync.ts"
import { PuzzleGenerator } from "./components/PuzzleGenerator.tsx"
import { PuzzleView } from "./components/PuzzleView.tsx"
import { LoadingOverlay } from "./components/LoadingOverlay.tsx"
import { LoginScreen } from "./components/LoginScreen.tsx"
import { AutoSaveIndicator } from "./components/AutoSaveIndicator.tsx"
import { PuzzleLibrary } from "./components/PuzzleLibrary.tsx"
import "./styles/global.css"
import "./styles/crossword.css"

export function App() {
  const [user, setUser] = useState<User | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFavorited, setIsFavorited] = useState(false)
  const { state, dispatch, cellClueMap } = usePuzzleState()
  const { route, navigate } = useRouter()

  // The current puzzle slug (from URL or from generated puzzle)
  const currentSlug = route.page === "puzzle" ? route.slug : state.puzzle?.slug
  const isCreator = user?.groups?.includes("creators") ?? false

  // Auto-save hook
  const syncStatus = useProgressSync(currentSlug, state, !!user)

  // Check for existing session on mount
  useEffect(() => {
    getCurrentUser()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false))
  }, [])

  // Load puzzle from URL slug
  useEffect(() => {
    if (route.page !== "puzzle" || !route.slug) return
    // Don't reload if we already have this puzzle
    if (state.puzzle?.slug === route.slug) return

    setIsLoading(true)
    setError(null)

    getPuzzleBySlug(route.slug)
      .then((data) => {
        dispatch({ type: "SET_PUZZLE", puzzle: data.puzzle })
        setIsFavorited(data.isFavorited)

        // Restore progress — prefer whichever is newer (server vs localStorage)
        const local = loadLocal(route.slug!)
        const serverProgress = data.progress

        let progressToRestore = serverProgress
        if (local && serverProgress) {
          // Use whichever has a more recent updatedAt
          const localTime = new Date(local.updatedAt).getTime()
          const serverTime = new Date(serverProgress.updatedAt).getTime()
          if (localTime > serverTime) {
            progressToRestore = local
          }
        } else if (local && !serverProgress) {
          progressToRestore = local
        }

        if (progressToRestore) {
          dispatch({
            type: "RESTORE_PROGRESS",
            cells: progressToRestore.cells,
            checkedCells: progressToRestore.checkedCells,
            revealedCells: progressToRestore.revealedCells,
            currentCell: progressToRestore.currentCell,
            direction: progressToRestore.direction,
            isComplete: progressToRestore.isComplete,
          })
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load puzzle")
      })
      .finally(() => setIsLoading(false))
  }, [route.page, route.slug])

  useKeyboardNav({
    puzzle: state.puzzle,
    currentCell: state.currentCell,
    direction: state.direction,
    cells: state.cells,
    cellClueMap,
    dispatch,
  })

  const handleGenerate = useCallback(async (gridSize: number, puzzleType: PuzzleType, gridStyle: GridStyle) => {
    setIsLoading(true)
    setError(null)
    try {
      const puzzle = await generatePuzzle({ gridSize, puzzleType, gridStyle })
      dispatch({ type: "SET_PUZZLE", puzzle })
      navigate(`/p/${puzzle.slug}`)
    } catch (err) {
      if (err instanceof AuthError) {
        setUser(null)
        return
      }
      setError(err instanceof Error ? err.message : "Failed to generate puzzle")
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, navigate])

  const handleNewPuzzle = useCallback(() => {
    dispatch({ type: "CLEAR_ALL" })
    navigate("/")
  }, [dispatch, navigate])

  const handleGoHome = useCallback(() => {
    navigate("/")
  }, [navigate])

  const handleDelete = useCallback(async () => {
    if (!currentSlug) return
    try {
      await deletePuzzle(currentSlug)
      dispatch({ type: "CLEAR_ALL" })
      navigate("/")
    } catch {
      // silently fail
    }
  }, [currentSlug, dispatch, navigate])

  const handleLogout = useCallback(async () => {
    await apiLogout()
    setUser(null)
    navigate("/")
  }, [navigate])

  // Auth loading state
  if (authLoading) {
    return (
      <div className="app">
        <div className="app-center">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  // Puzzle URL — allow viewing even without auth
  if (route.page === "puzzle" && route.slug) {
    // If puzzle is loading or loaded, show it
    if (isLoading && !state.puzzle) {
      return (
        <div className="app">
          <LoadingOverlay />
        </div>
      )
    }

    if (error && !state.puzzle) {
      return (
        <div className="app">
          <div className="app-center">
            <div className="error-message">{error}</div>
            <button className="btn" onClick={() => navigate("/")}>Back to home</button>
          </div>
        </div>
      )
    }

    if (state.puzzle) {
      return (
        <div className="app">
          {user ? (
            <div className="user-menu">
              {user.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="user-avatar"
                  referrerPolicy="no-referrer"
                />
              )}
              <span className="user-name">{user.name}</span>
              <button className="user-logout" onClick={handleLogout} title="Sign out">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3" />
                  <path d="M10 11l3-3-3-3" />
                  <path d="M13 8H6" />
                </svg>
              </button>
            </div>
          ) : (
            <button className="user-menu sign-in-btn" onClick={() => navigate("/")}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 2h3a1 1 0 011 1v10a1 1 0 01-1 1h-3" />
                <path d="M6 11l3-3-3-3" />
                <path d="M3 8h10" />
              </svg>
              <span>Sign in</span>
            </button>
          )}

          {isLoading && <LoadingOverlay />}

          <PuzzleView
            puzzle={state.puzzle}
            cells={state.cells}
            currentCell={state.currentCell}
            direction={state.direction}
            checkedCells={state.checkedCells}
            revealedCells={state.revealedCells}
            flashCells={state.flashCells}
            cellClueMap={cellClueMap}
            isComplete={state.isComplete}
            dispatch={dispatch}
            onNewPuzzle={isCreator ? handleNewPuzzle : undefined}
            onHome={handleGoHome}
            onDelete={user && state.puzzle?.creatorId === user.id ? handleDelete : undefined}
            isFavorited={isFavorited}
            onToggleFavorite={user ? setIsFavorited : undefined}
            slug={currentSlug}
          />

          {user && <AutoSaveIndicator status={syncStatus} />}
        </div>
      )
    }
  }

  // Not logged in — show login screen
  if (!user) {
    return (
      <div className="app">
        <LoginScreen onLogin={setUser} />
      </div>
    )
  }

  // Logged in, home page — show generator
  return (
    <div className="app">
      <div className="user-menu">
        {user.picture && (
          <img
            src={user.picture}
            alt={user.name}
            className="user-avatar"
            referrerPolicy="no-referrer"
          />
        )}
        <span className="user-name">{user.name}</span>
        <button className="user-logout" onClick={handleLogout} title="Sign out">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3" />
            <path d="M10 11l3-3-3-3" />
            <path d="M13 8H6" />
          </svg>
        </button>
      </div>

      {isLoading && <LoadingOverlay message="Generating your crossword..." hint="This may take a few seconds" />}

      <div className="app-center">
        {isCreator && <PuzzleGenerator onGenerate={handleGenerate} isLoading={isLoading} />}
        {error && <div className="error-message">{error}</div>}
        <PuzzleLibrary onNavigate={navigate} currentUserId={user?.id} />
      </div>
    </div>
  )
}
